import * as React from 'react';
import { Rendition, LinearSpineItem, isSpineItemInstalled } from '../../../types/epub';
import { Dimension } from '../../../types/common';
import { buildSpreadBlock } from '../../../utils/epub';
import PageIFrame from '../PageIFrame';
import { makeFrameIDForLinearPage } from '../../../helpers/pages';
import SheetScaler, { ScaleBehavior } from '../SheetScaler';
import { UIEventListeners } from '../../../../../../common/src/ts/types/uievent';
import { getGlobalFrameStore } from '../../../services/FrameStore';
import { FrameInfo } from '../../../../../../common/src/ts/types/FrameInfo';
import { UIModeState } from '../../../reducers/uimode';
import { defaultPageIFrameBackgrouncColor } from '../../../constants';
import { Rectangle } from 'ark-common';
import { getNotInstalledURLFullPath } from '../../../utils/browser';

export interface LinearPageProps {
  rootDir: string;
  rendition: Rendition;
  installedComponents: ReadonlyArray<string>;
  page: number;
  tabID: string;
  uiEventListeners: UIEventListeners;
  uimode: UIModeState;
}

const createIFrameForItem = (
  item: LinearSpineItem,
  frameInfo: FrameInfo,
  rootDir: string,
  uiEventListeners: UIEventListeners,
  installedComponents: ReadonlyArray<string>,
): [JSX.Element, Dimension] => {
  if (!item.fxl) {
    throw new Error('Two-page spread pages must be FXL.');
  }

  return [
    <PageIFrame
      key={item.path}
      type={'file'}
      frameInfo={frameInfo}
      width={item.width}
      height={item.height}
      style={defaultPageIFrameBackgrouncColor} // 見開き領域も塗るが既定として紙面iframeも塗る
      content={
        isSpineItemInstalled(item, installedComponents)
          ? `${rootDir}/${item.path}`
          : getNotInstalledURLFullPath()
      }
      pointerListeners={uiEventListeners} />,
    { width: item.width, height: item.height },
  ];
};

const createFrameInfo = (panelID: string, item: LinearSpineItem, opf: string): FrameInfo => {
  const reference: [string, string] = [opf, item.epubcfi];
  return {
    panelID,
    reference,
    frameID: makeFrameIDForLinearPage(panelID, item.linearIndex),
    shouldNotifyContentSize: false,
    shouldApplyReflowableContentSettings: false,
  };
};

const LinearPage: React.SFC<LinearPageProps> = (props) => {
  const { rootDir, rendition, installedComponents, uiEventListeners, tabID: panelID } = props;

  const iframes: JSX.Element[] = [];
  const frameIDs: { [position: string]: string } = {}; // position: left | right | center
  let spreadSize: Dimension;
  let align: 'flex-start' | 'center' | 'flex-end' = 'flex-start';

  const block = buildSpreadBlock(rendition, props.page);

  if (block.center !== undefined) {
    const frameInfo = createFrameInfo(panelID, block.center, rendition.opf);
    const [iframe, size] = createIFrameForItem(
      block.center,
      frameInfo,
      rootDir,
      uiEventListeners,
      installedComponents,
    );

    spreadSize = size;
    align = 'center';
    frameIDs['center'] = frameInfo.frameID;
    iframes.push(iframe);
  }

  if (block.left !== undefined) {
    const frameInfo = createFrameInfo(panelID, block.left, rendition.opf);
    const [iframe, size] = createIFrameForItem(
      block.left,
      frameInfo,
      rootDir,
      uiEventListeners,
      installedComponents,
    );

    spreadSize = { width: size.width * 2, height: size.height };
    frameIDs['left'] = frameInfo.frameID;
    iframes.push(iframe);
  }
  if (block.right !== undefined) {
    const frameInfo = createFrameInfo(panelID, block.right, rendition.opf);
    const [iframe, size] = createIFrameForItem(
      block.right,
      frameInfo,
      rootDir,
      uiEventListeners,
      installedComponents,
    );

    // Assumes that both left and right pages have same widths.
    spreadSize = { width: size.width * 2, height: size.height };
    if (block.left === undefined) {
      align = 'flex-end';
    }
    frameIDs['right'] = frameInfo.frameID;
    iframes.push(iframe);
  }

  const frameStore = getGlobalFrameStore();
  const onScaleApply = (
    visibleRect: Rectangle,
    visualScale: number,
    tabPanelAreaRect: Rectangle,
  ) => {
    for (const position in frameIDs) {
      const singleWidth = spreadSize.width / 2;
      const rect = { ...visibleRect };
      switch (position) {
        case 'left':
          rect.width = Math.min(rect.width, singleWidth);
          break;
        case 'right': {
          const right = rect.x + rect.width - singleWidth;
          rect.x = Math.max(0, rect.x - singleWidth);
          rect.width = right - rect.x;
          break;
        }
      }

      const { width, height } = tabPanelAreaRect;
      frameStore.notifyVisualProperties(
        frameIDs[position],
        rect,
        visualScale,
        { width, height },
        false,
      );
    }
  };

  const pageContainerStyle: React.CSSProperties = {
    ...defaultPageIFrameBackgrouncColor,
    display: 'flex',
    width: `${spreadSize!.width}px`,
    justifyContent: align,
  };

  return (
    <SheetScaler
      behavior={ScaleBehavior.Transform}
      panelID={props.tabID}
      uiEventListeners={props.uiEventListeners}
      uimode={props.uimode}
      onScaleApply={onScaleApply}>
      <div style={pageContainerStyle}>
        {iframes}
      </div>
    </SheetScaler>
  );
};

export default LinearPage;
